import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Typography,
  Box,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { customerApi } from '../../../api/customerApi';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    minWidth: 400,
  },
}));

const ExportDialog = ({ open, onClose }) => {
  const [exportType, setExportType] = useState('apprenants');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleExportTypeChange = (event) => {
    setExportType(event.target.value);
  };

  const handleExport = async () => {
    setLoading(true);
    setError('');

    try {
      const accessToken = localStorage.getItem('accessToken');
      const response = await customerApi.exportCustomers(accessToken, exportType);
      
      if (response.status === 200) {
        // Créer et télécharger le fichier CSV
        // Le backend renvoie désormais le CSV encodé en UTF-8
        const blob = new Blob([response.data], { type: 'text/csv;charset=UTF-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        
        link.setAttribute('href', url);
        link.setAttribute('download', `export_${exportType}_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        onClose();
      } else {
        setError('Erreur lors de l\'export. Veuillez réessayer.');
      }
    } catch (err) {
      console.error('Erreur lors de l\'export:', err);
      setError('Erreur lors de l\'export. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError('');
      setExportType('apprenants');
      onClose();
    }
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Exporter les données
      </DialogTitle>
      
      <DialogContent>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Choisissez le type de données à exporter :
        </Typography>
        
        <RadioGroup value={exportType} onChange={handleExportTypeChange}>
          <FormControlLabel
            value="apprenants"
            control={<Radio />}
            label="Apprenants uniquement"
          />
          <FormControlLabel
            value="prospects"
            control={<Radio />}
            label="Prospects uniquement"
          />
          <FormControlLabel
            value="all"
            control={<Radio />}
            label="Toute la base (apprenants + prospects)"
          />
        </RadioGroup>

        <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
          <Typography variant="body2" color="textSecondary">
            <strong>Fichier CSV contenant :</strong>
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
            Lien syndays, civilité, nom, nom de jeune fille, prénom, adresse email, téléphone, 
            téléphone fixe, date de naissance, ville de naissance, adresse, code postal, ville, 
            département, région, pays de résidence, profession, spécialité, mode d'exercice, 
            adeli_rpps, remplaçant, ne pas appeler, siret, profil linkedIn, établissement, 
            canal préféré de contact, moment préféré de contact, import
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Annuler
        </Button>
        <Button
          onClick={handleExport}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Export en cours...' : 'Exporter'}
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

ExportDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ExportDialog; 